import React from "react";
import { BarWithRoundCorners, Card, EmptyState, LongArrowIcon, RiskStatusIcon, Section, Tooltip, } from "@sscs/ui";
import { cssVars } from "@sscs/ui/styles";
import { Bar, CartesianGrid, Cell, LabelList, BarChart as RechartsBarChart, Tooltip as RechartsTooltip, ResponsiveContainer, XAxis, YAxis, } from "recharts";
import { ASSESSMENT_TYPE_TO_TITLE } from "../../../constants";
import styles from "./componentAgeTimeline.module.scss";
import { TIMELINES_UNKNOWN_TIMESTAMP, componentAgeHasIssues, formatComponentCount, formatTimelineTimestamp, getComponentCountsFromTimeline, getHalfComponentsIntroducedBefore, getMostSevereRiskStatus, getTimelineDataByYear, isUnknownTimelinesTimestamp, } from "./utils";
const STATUS_TO_TEXT = {
    fail: "Failed",
    warning: "Warned",
    pass: "Passed",
};
const barTooltipPayloadIsTimelineData = (payload) => {
    const timelineDataByYear = payload;
    return (typeof timelineDataByYear.year === "string" &&
        typeof timelineDataByYear.componentCount === "number" &&
        typeof timelineDataByYear.riskCounts === "object");
};
const CustomChartTooltip = ({ active, payload, }) => {
    if (!active || !payload || !payload.length) {
        return null;
    }
    const [barPayload] = payload;
    if (!barTooltipPayloadIsTimelineData(barPayload.payload)) {
        throw new Error(`Invalid bar tooltip payload: ${barPayload.payload}`);
    }
    const { year, componentCount, riskCounts } = barPayload.payload;
    return (React.createElement(Tooltip.CardSurface, { header: `Year ${isUnknownTimelinesTimestamp(year) ? "undetermined" : year}` },
        React.createElement("div", { className: styles["chart-tooltip-content"] },
            React.createElement("div", null,
                componentCount,
                " component",
                componentCount > 1 ? "s" : "",
                " introduced"),
            React.createElement(Section, null,
                React.createElement(Section.Title, null, "Components causing risks"),
                React.createElement("div", { className: styles["chart-tooltip-risks"] }, Object.keys(riskCounts.byCategory).map((category) => {
                    const mostSevereRisksCount = getMostSevereRiskStatus(riskCounts.byCategory[category].byStatus);
                    return (React.createElement(React.Fragment, { key: category },
                        React.createElement("span", null,
                            ASSESSMENT_TYPE_TO_TITLE[category],
                            ":"),
                        React.createElement("strong", { className: styles["chart-tooltip-total-risks"] }, riskCounts.byCategory[category].total),
                        React.createElement("span", { className: styles["chart-tooltip-most-severe-risks-container"] }, mostSevereRisksCount && (React.createElement(React.Fragment, null,
                            React.createElement("span", { className: styles["chart-tooltip-most-severe-risks-arrow"] },
                                React.createElement(LongArrowIcon, null)),
                            React.createElement("span", { className: styles["chart-tooltip-most-severe-risks"] },
                                React.createElement(RiskStatusIcon, { status: mostSevereRisksCount.status, disabled: false }),
                                React.createElement("strong", null,
                                    mostSevereRisksCount.count,
                                    " ",
                                    STATUS_TO_TEXT[mostSevereRisksCount.status])))))));
                }))))));
};
const BarChart = ({ data, onBarClick }) => {
    const maxComponentCountDigits = Math.max(...data.map((timelineData) => timelineData.componentCount.toString().length));
    // Calculate bar chart left margin and y-axis label offset from the data
    // to ensure the optimal spacing between the label and the y-axis ticks
    const barChartMarginLeft = -8 + 8 * maxComponentCountDigits;
    const yAxisLabelOffset = 24 - 8 * maxComponentCountDigits;
    return (React.createElement("div", { className: styles["chart-wrapper"] },
        React.createElement(ResponsiveContainer, { width: "99%", height: "100%", minHeight: 320 },
            React.createElement(RechartsBarChart, { data: data, margin: {
                    left: barChartMarginLeft,
                    right: 4,
                    bottom: 16,
                }, overflow: "visible" },
                React.createElement(CartesianGrid, { strokeDasharray: "3", vertical: false, stroke: cssVars.dataviz_grid }),
                React.createElement(XAxis, { dataKey: "year", interval: "preserveStart", tickFormatter: (value) => isUnknownTimelinesTimestamp(value) ? "?" : value, fill: cssVars.text_medium, label: {
                        value: "Component introduction",
                        position: "insideBottom",
                        offset: -16,
                        fill: cssVars.text_primary,
                    } }),
                React.createElement(YAxis, { dataKey: "componentCount", allowDecimals: false, tickFormatter: (value) => value.toLocaleString(), fill: cssVars.text_medium, label: {
                        value: "Number of introduced components",
                        angle: -90,
                        position: "insideBottomLeft",
                        offset: yAxisLabelOffset,
                        fill: cssVars.text_primary,
                    } }),
                React.createElement(RechartsTooltip, { content: CustomChartTooltip, cursor: { fill: cssVars.surface_hover } }),
                React.createElement(Bar, { dataKey: "componentCount", minPointSize: 5, barSize: 32, "aria-label": "Click to get more details", shape: BarWithRoundCorners, onClick: onBarClick
                        ? ({ payload }) => {
                            if (!barTooltipPayloadIsTimelineData(payload)) {
                                throw new Error(`Invalid bar payload: ${payload}`);
                            }
                            if (componentAgeHasIssues(payload.riskCounts)) {
                                onBarClick(payload.year, payload.year === TIMELINES_UNKNOWN_TIMESTAMP);
                            }
                        }
                        : undefined },
                    React.createElement(LabelList, { dataKey: "componentCount", position: "top", fill: cssVars.text_medium, formatter: formatComponentCount }),
                    data.map((item) => (React.createElement(Cell, { cursor: componentAgeHasIssues(item.riskCounts) ? "pointer" : undefined, key: item.year, fill: item.componentCount === 0 ? "none" : cssVars.dataviz_neutral }))))))));
};
const getTimelineDetail = (timeline, componentsWithKnownAgeCount) => {
    if (componentsWithKnownAgeCount <= 0) {
        return undefined;
    }
    if (Object.keys(timeline).length === 1) {
        return `All components were introduced in ${formatTimelineTimestamp(Object.keys(timeline)[0])}`;
    }
    return `50% of components were introduced before ${getHalfComponentsIntroducedBefore(timeline, componentsWithKnownAgeCount)}`;
};
const ComponentAgeTimeline = ({ timeline, helperText, onBarClick, }) => {
    const { totalComponentsCount, componentsWithKnownAgeCount } = getComponentCountsFromTimeline(timeline);
    return (React.createElement(Card.Card, null,
        React.createElement(Card.Title, { details: [getTimelineDetail(timeline, componentsWithKnownAgeCount)], actions: helperText }, "Component age"),
        React.createElement(Card.Content, null,
            React.createElement("div", { className: styles.content }, totalComponentsCount > 0 ? (React.createElement(BarChart, { data: getTimelineDataByYear(timeline), onBarClick: onBarClick })) : (React.createElement(EmptyState, { subject: "components" }))))));
};
export default ComponentAgeTimeline;
//# sourceMappingURL=ComponentAgeTimeline.js.map